import { UnitConverter } from '../base/UnitConverter';
import { UnitOptions } from '../constants/unit.options';
import { CountPolicies, CountPolicy } from '../policy';
import { QuantityResult } from '../types';

export class TimeUnitConverter extends UnitConverter<
  (typeof UnitOptions)['Time'][number]['value']
> {
  // 싱글턴 인스턴스
  private static instance: TimeUnitConverter;

  private constructor() {
    super();
  }

  static getInstance(): TimeUnitConverter {
    if (!TimeUnitConverter.instance) {
      TimeUnitConverter.instance = new TimeUnitConverter();
    }
    return TimeUnitConverter.instance;
  }

  protected units = [
    'nanosecond (ns)',
    'microsecond (μs)',
    'millisecond (ms)',
    'centisecond (cs)',
    'second (s)',
    'minute (min)',
    'hour (hr)',
    'day',
    'week (wk)',
    'month',
    'year',
  ] as const;

  protected unitToBase = {
    'nanosecond (ns)': 1e-9,
    'microsecond (μs)': 1e-6,
    'millisecond (ms)': 1e-3,
    'centisecond (cs)': 1e-2,
    'second (s)': 1,
    'minute (min)': 60,
    'hour (hr)': 3_600,
    day: 86_400,
    'week (wk)': 604_800,
    month: 2_629_746,
    year: 31_556_952,
  };

  // 정책 기본값 disabled로 override
  override resolveCountPolicy(countPolicy?: CountPolicy): CountPolicy {
    return countPolicy ?? CountPolicies.countConversionEnabled;
  }

  // centisecond를 제외한 단위 배열 (성능 최적화를 위해 정적 정의)
  private static readonly UNITS_WITHOUT_CS = [
    'nanosecond (ns)',
    'microsecond (μs)',
    'millisecond (ms)',
    'second (s)',
    'minute (min)',
    'hour (hr)',
    'day',
    'week (wk)',
    'month',
    'year',
  ] as const;

  override convertToBestUnit(
    _value: number,
    _fromUnit: string,
    countPolicy?: CountPolicy,
  ): QuantityResult {
    const policy = this.resolveCountPolicy(countPolicy);

    let value = _value;
    let fromUnit = _fromUnit;

    if (!this.isValidUnit(fromUnit)) return policy(value, fromUnit);

    // centisecond (cs) 단위는 millisecond (ms) 단위로 변환
    if (fromUnit === 'centisecond (cs)') {
      value = value * 10;
      fromUnit = 'millisecond (ms)';
    }

    // value가 0이면 fromUnit 그대로 반환
    if (value === 0) return policy(value, fromUnit);

    const baseValue = value * this.unitToBase[fromUnit];
    const absBase = Math.abs(baseValue);

    // centisecond를 제외한 단위들로 최적 단위 찾기
    for (let i = TimeUnitConverter.UNITS_WITHOUT_CS.length - 1; i >= 0; i--) {
      const unit = TimeUnitConverter.UNITS_WITHOUT_CS[i];
      const ratio = this.unitToBase[unit];
      if (absBase >= ratio) return policy(baseValue / ratio, unit);
    }

    // 가장 작은 단위 사용 (centisecond 제외)
    const smallest = TimeUnitConverter.UNITS_WITHOUT_CS[0];
    return policy(baseValue / this.unitToBase[smallest], smallest);
  }

  override convertToLargerBestUnit(
    value: number,
    fromUnit: string,
    countPolicy?: CountPolicy,
  ): QuantityResult {
    const policy = this.resolveCountPolicy(countPolicy);

    // ms, cs 단위는 모두 s로 변경하고, 그외의 단위는 그대로 보여준다.
    if (fromUnit === 'millisecond (ms)') {
      return policy(value / 1000, 'second (s)');
    }
    if (fromUnit === 'centisecond (cs)') {
      return policy(value / 100, 'second (s)');
    }
    return policy(value, fromUnit);
  }
}
